import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { NIMNY_AVATAR } from '../data/translations';
import { useLanguage } from '../context/LanguageContext';
import { NightAmbience } from '../components/NimnyLanding';

// Terms of Use content in all languages
const termsContent = {
  en: {
    title: 'Terms of Use',
    backToHome: 'Back to home',
    lastUpdated: 'Last updated: April 2026',
    intro: 'These Terms of Use ("Terms") govern your use of Nimny LLC ("Nimny", "we", "us", or "our") websites, mobile applications, and related services (the "Service").',
    disclaimer: 'By accessing or using the Service, you agree to these Terms. If you do not agree, do not use the Service.',
    supportButton: 'Contact Support',
    sections: [
      {
        title: '1. Eligibility and Accounts',
        content: [
          'You must be at least 18 years old (or the age of majority in your jurisdiction) to create an account.',
          'You are responsible for:\n• maintaining the confidentiality of your account credentials\n• all activities under your account',
          'We may suspend or terminate accounts that violate these Terms.'
        ]
      },
      {
        title: '2. Use of the Service',
        content: [
          'Nimny provides personalized stories, audio content, and related features for family use.',
          'You may use the Service for personal, non-commercial purposes only.',
          'You agree not to:\n• copy, reproduce, distribute, or modify content\n• reverse engineer or attempt to extract source code\n• use the Service for unlawful or harmful purposes'
        ]
      },
      {
        title: '3. Subscriptions and Payments',
        content: [
          'Some features require a paid subscription.',
          '• Subscriptions automatically renew unless cancelled before the end of the billing period\n• You may cancel at any time through Apple App Store, Google Play, or your payment provider\n• Payments and refunds are handled by the platform where the purchase was made',
          'We do not control Apple or Google refund decisions.'
        ]
      },
      {
        title: '4. Acceptable Use and Fair Usage',
        content: [
          'The Service is intended for normal personal use.',
          'We may apply reasonable limits to:\n• story generation\n• audio usage\n• feature access',
          'We may adjust quality, length, or availability to maintain performance and fairness.'
        ]
      },
      {
        title: '5. Content and Intellectual Property',
        content: [
          'All content provided by Nimny (stories, audio, visuals) is owned by Nimny LLC or its licensors.',
          'You may not:\n• copy or redistribute content\n• use content commercially',
          'You retain rights to your inputs, but Nimny may use anonymized data to improve the Service.'
        ]
      },
      {
        title: '6. Children and Parental Responsibility',
        content: [
          'Nimny is designed for families.',
          'Children must use the Service under supervision of a parent or guardian.',
          'Parents are responsible for:\n• how the Service is used\n• content generated\n• data provided about children'
        ]
      },
      {
        title: '7. AI-Generated Content',
        content: [
          'The Service uses artificial intelligence to generate stories, audio, and other content.',
          'AI-generated content:\n• may be inaccurate, incomplete, or unexpected\n• may not be suitable for all users or situations',
          'Nimny provides such content for entertainment purposes only.',
          'You are solely responsible for reviewing and determining whether content is appropriate for your child or family.'
        ]
      },
      {
        title: '8. No Guarantee of Results',
        content: [
          'The Service is intended to provide engaging and calming storytelling experiences.',
          'However, Nimny does not guarantee any specific outcomes, including:\n• improved sleep\n• emotional responses\n• behavioral changes'
        ]
      },
      {
        title: '9. Disclaimer of Warranties ("AS IS")',
        content: [
          'The Service is provided "as is" and "as available" without warranties of any kind.',
          'We do not guarantee that:\n• the Service will be uninterrupted or error-free\n• the Service will always be available or secure\n• generated content will meet your expectations',
          'Your use of the Service is at your own risk.'
        ]
      },
      {
        title: '10. Limitation of Liability',
        content: [
          'To the maximum extent permitted by law, Nimny LLC and its affiliates shall not be liable for:\n• indirect, incidental, special, or consequential damages\n• loss of data, use, goodwill, or profits\n• issues arising from AI-generated content\n• third-party platform issues (Apple, Google, Stripe, etc.)',
          'In no event shall Nimny LLC\'s total liability exceed the amount you paid to Nimny in the twelve (12) months preceding the claim.'
        ]
      },
      {
        title: '11. Indemnification',
        content: [
          'You agree to defend, indemnify, and hold harmless Nimny LLC and its affiliates from any claims, damages, or expenses arising from:\n• your use of the Service\n• your violation of these Terms\n• your violation of third-party rights\n• any content you generate or submit'
        ]
      },
      {
        title: '12. Third-Party Services',
        content: [
          'The Service may rely on third-party providers, including:\n• Apple App Store / Google Play\n• payment processors\n• analytics and infrastructure providers',
          'We are not responsible for third-party services or their policies.'
        ]
      },
      {
        title: '13. Service Availability',
        content: [
          'We may modify, suspend, or discontinue any part of the Service at any time, with or without notice.',
          'We do not guarantee continuous availability.'
        ]
      },
      {
        title: '14. Force Majeure',
        content: [
          'Nimny shall not be liable for delays or failures caused by events beyond its control, including:\n• natural disasters\n• internet outages\n• failures of third-party services'
        ]
      },
      {
        title: '15. Dispute Resolution',
        content: [
          'Any disputes shall be resolved through binding arbitration on an individual basis.',
          'You agree:\n• not to participate in class actions\n• to resolve disputes individually'
        ]
      },
      {
        title: '16. Changes to Terms',
        content: [
          'We may update these Terms at any time.',
          'Continued use of the Service means you accept the updated Terms.'
        ]
      },
      {
        title: '17. Governing Law',
        content: 'These Terms are governed by the laws of the State of Florida, United States.'
      },
      {
        title: '18. Contact',
        content: 'For questions, please use the form below.'
      }
    ]
  },
  ru: {
    title: 'Условия использования',
    backToHome: 'На главную',
    lastUpdated: 'Последнее обновление: апрель 2026',
    intro: 'Настоящие Условия использования («Условия») регулируют использование вами веб-сайтов, мобильных приложений и связанных сервисов Nimny LLC («Nimny», «мы», «нас» или «наш») (далее — «Сервис»).',
    disclaimer: 'Получая доступ к Сервису или используя его, вы соглашаетесь с настоящими Условиями. Если вы не согласны — не используйте Сервис.',
    supportButton: 'Связаться со службой поддержки',
    sections: [
      {
        title: '1. Право пользования и аккаунты',
        content: [
          'Для создания аккаунта вам должно быть не менее 18 лет (или возраст совершеннолетия в вашей юрисдикции).',
          'Вы несёте ответственность за:\n• сохранение конфиденциальности ваших учётных данных\n• все действия под вашим аккаунтом',
          'Мы можем приостановить или удалить аккаунты, нарушающие настоящие Условия.'
        ]
      },
      {
        title: '2. Использование Сервиса',
        content: [
          'Nimny предоставляет персонализированные истории, аудиоконтент и связанные функции для семейного использования.',
          'Вы можете использовать Сервис только в личных, некоммерческих целях.',
          'Вы соглашаетесь не:\n• копировать, воспроизводить, распространять или изменять контент\n• осуществлять обратную разработку или извлекать исходный код\n• использовать Сервис в незаконных или вредоносных целях'
        ]
      },
      {
        title: '3. Подписки и платежи',
        content: [
          'Некоторые функции требуют платной подписки.',
          '• Подписки автоматически продлеваются, если не отменены до окончания платёжного периода\n• Вы можете отменить подписку в любое время через Apple App Store, Google Play или вашего платёжного провайдера\n• Платежи и возвраты обрабатываются платформой, на которой была совершена покупка',
          'Мы не контролируем решения о возврате средств Apple или Google.'
        ]
      },
      {
        title: '4. Допустимое использование и справедливое использование',
        content: [
          'Сервис предназначен для обычного личного использования.',
          'Мы можем применять разумные ограничения на:\n• генерацию историй\n• использование аудио\n• доступ к функциям',
          'Мы можем корректировать качество, длину или доступность для поддержания производительности и справедливости.'
        ]
      },
      {
        title: '5. Контент и интеллектуальная собственность',
        content: [
          'Весь контент, предоставляемый Nimny (истории, аудио, визуальные материалы), принадлежит Nimny LLC или её лицензиарам.',
          'Вы не можете:\n• копировать или распространять контент\n• использовать контент в коммерческих целях',
          'Вы сохраняете права на ваши входные данные, но Nimny может использовать анонимизированные данные для улучшения Сервиса.'
        ]
      },
      {
        title: '6. Дети и ответственность родителей',
        content: [
          'Nimny создан для семей.',
          'Дети должны использовать Сервис под присмотром родителя или опекуна.',
          'Родители несут ответственность за:\n• то, как используется Сервис\n• сгенерированный контент\n• данные, предоставленные о детях'
        ]
      },
      {
        title: '7. Контент, созданный ИИ',
        content: [
          'Сервис использует искусственный интеллект для генерации историй, аудио и другого контента.',
          'Контент, созданный ИИ:\n• может быть неточным, неполным или неожиданным\n• может не подходить для всех пользователей или ситуаций',
          'Nimny предоставляет такой контент только в развлекательных целях.',
          'Вы несёте единоличную ответственность за проверку и определение того, подходит ли контент для вашего ребёнка или семьи.'
        ]
      },
      {
        title: '8. Отсутствие гарантии результатов',
        content: [
          'Сервис предназначен для создания увлекательных и успокаивающих историй.',
          'Однако Nimny не гарантирует каких-либо конкретных результатов, включая:\n• улучшение сна\n• эмоциональные реакции\n• изменения поведения'
        ]
      },
      {
        title: '9. Отказ от гарантий («КАК ЕСТЬ»)',
        content: [
          'Сервис предоставляется «как есть» и «по мере доступности» без каких-либо гарантий.',
          'Мы не гарантируем, что:\n• работа Сервиса будет непрерывной или без ошибок\n• Сервис всегда будет доступен или безопасен\n• сгенерированный контент оправдает ваши ожидания',
          'Вы используете Сервис на свой страх и риск.'
        ]
      },
      {
        title: '10. Ограничение ответственности',
        content: [
          'В максимально разрешённой законом степени Nimny LLC и её аффилированные лица не несут ответственности за:\n• косвенные, случайные, особые или последующие убытки\n• потерю данных, использования, репутации или прибыли\n• проблемы, возникающие в связи с контентом, созданным ИИ\n• проблемы сторонних платформ (Apple, Google, Stripe и др.)',
          'Ни при каких обстоятельствах общая ответственность Nimny LLC не превысит сумму, уплаченную вами Nimny за двенадцать (12) месяцев, предшествующих предъявлению требования.'
        ]
      },
      {
        title: '11. Возмещение ущерба',
        content: [
          'Вы соглашаетесь защищать, ограждать от ответственности и возмещать ущерб Nimny LLC и её аффилированным лицам в связи с любыми претензиями, убытками или расходами, возникающими из:\n• вашего использования Сервиса\n• вашего нарушения настоящих Условий\n• вашего нарушения прав третьих лиц\n• любого контента, который вы создаёте или отправляете'
        ]
      },
      {
        title: '12. Сторонние сервисы',
        content: [
          'Сервис может зависеть от сторонних поставщиков, включая:\n• Apple App Store / Google Play\n• платёжные процессоры\n• провайдеров аналитики и инфраструктуры',
          'Мы не несём ответственности за сторонние сервисы или их политики.'
        ]
      },
      {
        title: '13. Доступность Сервиса',
        content: [
          'Мы можем изменять, приостанавливать или прекращать работу любой части Сервиса в любое время, с уведомлением или без.',
          'Мы не гарантируем непрерывную доступность.'
        ]
      },
      {
        title: '14. Форс-мажор',
        content: [
          'Nimny не несёт ответственности за задержки или сбои, вызванные событиями, находящимися вне её контроля, включая:\n• стихийные бедствия\n• сбои в работе интернета\n• сбои сторонних сервисов'
        ]
      },
      {
        title: '15. Разрешение споров',
        content: [
          'Любые споры разрешаются путём обязательного арбитража на индивидуальной основе.',
          'Вы соглашаетесь:\n• не участвовать в коллективных исках\n• разрешать споры индивидуально'
        ]
      },
      {
        title: '16. Изменения условий',
        content: [
          'Мы можем обновлять настоящие Условия в любое время.',
          'Продолжение использования Сервиса означает, что вы принимаете обновлённые Условия.'
        ]
      },
      {
        title: '17. Применимое право',
        content: 'Настоящие Условия регулируются законодательством штата Флорида, США.'
      },
      {
        title: '18. Контакты',
        content: 'По вопросам воспользуйтесь, пожалуйста, формой ниже.'
      }
    ]
  },
  es: {
    title: 'Términos de Uso',
    backToHome: 'Volver al inicio',
    lastUpdated: 'Última actualización: abril 2026',
    intro: 'Estos Términos de Uso ("Términos") rigen tu uso de los sitios web, aplicaciones móviles y servicios relacionados de Nimny LLC ("Nimny", "nosotros", "nos" o "nuestro") (el "Servicio").',
    disclaimer: 'Al acceder o usar el Servicio, aceptas estos Términos. Si no estás de acuerdo, no uses el Servicio.',
    supportButton: 'Contactar con soporte',
    sections: [
      {
        title: '1. Elegibilidad y cuentas',
        content: [
          'Debes tener al menos 18 años (o la mayoría de edad en tu jurisdicción) para crear una cuenta.',
          'Eres responsable de:\n• mantener la confidencialidad de las credenciales de tu cuenta\n• todas las actividades realizadas en tu cuenta',
          'Podemos suspender o cancelar cuentas que violen estos Términos.'
        ]
      },
      {
        title: '2. Uso del Servicio',
        content: [
          'Nimny ofrece historias personalizadas, contenido de audio y funciones relacionadas para uso familiar.',
          'Puedes usar el Servicio únicamente con fines personales y no comerciales.',
          'Aceptas no:\n• copiar, reproducir, distribuir o modificar contenido\n• realizar ingeniería inversa o intentar extraer código fuente\n• usar el Servicio para fines ilegales o dañinos'
        ]
      },
      {
        title: '3. Suscripciones y pagos',
        content: [
          'Algunas funciones requieren una suscripción de pago.',
          '• Las suscripciones se renuevan automáticamente a menos que se cancelen antes del final del período de facturación\n• Puedes cancelar en cualquier momento a través de Apple App Store, Google Play o tu proveedor de pagos\n• Los pagos y reembolsos son gestionados por la plataforma donde se realizó la compra',
          'No controlamos las decisiones de reembolso de Apple o Google.'
        ]
      },
      {
        title: '4. Uso aceptable y uso justo',
        content: [
          'El Servicio está pensado para uso personal normal.',
          'Podemos aplicar límites razonables a:\n• generación de historias\n• uso de audio\n• acceso a funciones',
          'Podemos ajustar la calidad, longitud o disponibilidad para mantener el rendimiento y la equidad.'
        ]
      },
      {
        title: '5. Contenido y propiedad intelectual',
        content: [
          'Todo el contenido proporcionado por Nimny (historias, audio, visuales) es propiedad de Nimny LLC o de sus licenciantes.',
          'No puedes:\n• copiar o redistribuir contenido\n• usar el contenido comercialmente',
          'Conservas los derechos sobre tus entradas, pero Nimny puede usar datos anonimizados para mejorar el Servicio.'
        ]
      },
      {
        title: '6. Niños y responsabilidad parental',
        content: [
          'Nimny está diseñado para familias.',
          'Los niños deben usar el Servicio bajo la supervisión de un padre o tutor.',
          'Los padres son responsables de:\n• cómo se usa el Servicio\n• el contenido generado\n• los datos proporcionados sobre niños'
        ]
      },
      {
        title: '7. Contenido generado por IA',
        content: [
          'El Servicio utiliza inteligencia artificial para generar historias, audio y otros contenidos.',
          'El contenido generado por IA:\n• puede ser inexacto, incompleto o inesperado\n• puede no ser adecuado para todos los usuarios o situaciones',
          'Nimny proporciona dicho contenido únicamente con fines de entretenimiento.',
          'Eres el único responsable de revisar y determinar si el contenido es apropiado para tu hijo o familia.'
        ]
      },
      {
        title: '8. Sin garantía de resultados',
        content: [
          'El Servicio está diseñado para ofrecer experiencias de narración atractivas y relajantes.',
          'Sin embargo, Nimny no garantiza ningún resultado específico, incluidos:\n• mejoras en el sueño\n• respuestas emocionales\n• cambios de comportamiento'
        ]
      },
      {
        title: '9. Exención de garantías ("TAL CUAL")',
        content: [
          'El Servicio se proporciona "tal cual" y "según disponibilidad" sin garantías de ningún tipo.',
          'No garantizamos que:\n• el Servicio sea ininterrumpido o libre de errores\n• el Servicio esté siempre disponible o seguro\n• el contenido generado cumpla tus expectativas',
          'El uso del Servicio es bajo tu propio riesgo.'
        ]
      },
      {
        title: '10. Limitación de responsabilidad',
        content: [
          'En la medida máxima permitida por la ley, Nimny LLC y sus afiliadas no serán responsables por:\n• daños indirectos, incidentales, especiales o consecuentes\n• pérdida de datos, uso, reputación o beneficios\n• problemas derivados del contenido generado por IA\n• problemas de plataformas de terceros (Apple, Google, Stripe, etc.)',
          'En ningún caso la responsabilidad total de Nimny LLC excederá la cantidad que hayas pagado a Nimny en los doce (12) meses anteriores a la reclamación.'
        ]
      },
      {
        title: '11. Indemnización',
        content: [
          'Aceptas defender, indemnizar y eximir de responsabilidad a Nimny LLC y sus afiliadas de cualquier reclamación, daño o gasto derivado de:\n• tu uso del Servicio\n• tu incumplimiento de estos Términos\n• tu violación de derechos de terceros\n• cualquier contenido que generes o envíes'
        ]
      },
      {
        title: '12. Servicios de terceros',
        content: [
          'El Servicio puede depender de proveedores externos, incluidos:\n• Apple App Store / Google Play\n• procesadores de pago\n• proveedores de analítica e infraestructura',
          'No somos responsables de los servicios de terceros ni de sus políticas.'
        ]
      },
      {
        title: '13. Disponibilidad del Servicio',
        content: [
          'Podemos modificar, suspender o descontinuar cualquier parte del Servicio en cualquier momento, con o sin previo aviso.',
          'No garantizamos la disponibilidad continua.'
        ]
      },
      {
        title: '14. Fuerza mayor',
        content: [
          'Nimny no será responsable de retrasos o fallos causados por eventos fuera de su control, incluidos:\n• desastres naturales\n• interrupciones de internet\n• fallos de servicios de terceros'
        ]
      },
      {
        title: '15. Resolución de disputas',
        content: [
          'Cualquier disputa se resolverá mediante arbitraje vinculante de forma individual.',
          'Aceptas:\n• no participar en demandas colectivas\n• resolver las disputas de forma individual'
        ]
      },
      {
        title: '16. Cambios en los Términos',
        content: [
          'Podemos actualizar estos Términos en cualquier momento.',
          'El uso continuado del Servicio significa que aceptas los Términos actualizados.'
        ]
      },
      {
        title: '17. Ley aplicable',
        content: 'Estos Términos se rigen por las leyes del Estado de Florida, Estados Unidos.'
      },
      {
        title: '18. Contacto',
        content: 'Para consultas, utiliza el formulario a continuación.'
      }
    ]
  }
};

const TermsPage = () => {
  const { language, setLanguage } = useLanguage();
  const location = useLocation();

  // Detect language from URL path
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/ru/')) {
      setLanguage('ru');
    } else if (path.startsWith('/es/')) {
      setLanguage('es');
    } else {
      setLanguage('en');
    }
  }, [location.pathname, setLanguage]);

  const content = termsContent[language] || termsContent.en;

  // Add canonical and hreflang tags
  useEffect(() => {
    const langCode = language === 'ru' ? 'ru' : language === 'es' ? 'es' : 'en';
    const canonicalUrl = language === 'en' ? 'https://readnimny.com/terms' : `https://readnimny.com/${langCode}/terms`;

    document.title = `${content.title} – Nimny`;

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    // Hreflang tags
    const hreflangData = [
      { lang: 'en', href: 'https://readnimny.com/terms' },
      { lang: 'es', href: 'https://readnimny.com/es/terms' },
      { lang: 'ru', href: 'https://readnimny.com/ru/terms' },
      { lang: 'x-default', href: 'https://readnimny.com/terms' }
    ];

    document.querySelectorAll('link[hreflang]').forEach(el => el.remove());
    hreflangData.forEach(({ lang, href }) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = lang;
      link.href = href;
      document.head.appendChild(link);
    });

    return () => {
      document.querySelectorAll('link[hreflang]').forEach(el => el.remove());
    };
  }, [language, content.title]);

  const renderContent = (contentItem) => {
    if (Array.isArray(contentItem)) {
      return contentItem.map((paragraph, i) => (
        <p key={i} className="mb-3 whitespace-pre-line">{paragraph}</p>
      ));
    }
    return <p className="whitespace-pre-line">{contentItem}</p>;
  };

  return (
    <div className="min-h-screen relative">
      {/* Night sky background */}
      <NightAmbience />

      <header className="relative z-10 py-6 px-6">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-amber-200/70 hover:text-amber-200 transition-colors font-sans" data-testid="terms-back-link">
            <ArrowLeft className="w-4 h-4" />
            {content.backToHome}
          </Link>
        </div>
      </header>

      <main className="relative z-10 px-6 pb-20">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-amber-200/30">
              <img src={NIMNY_AVATAR} alt="Nimny" className="w-full h-full object-cover" style={{ transform: 'scale(1.2)', objectPosition: 'center 35%' }} />
            </div>
            <span className="font-sans text-2xl text-[#f5ebe0]/90">Nimny</span>
          </div>

          <h1 className="font-sans text-3xl md:text-4xl text-[#f5ebe0]/90 text-center mb-4" data-testid="terms-title">
            {content.title}
          </h1>

          <p className="text-amber-200/50 text-sm text-center mb-10 font-sans">
            {content.lastUpdated}
          </p>

          <div className="bg-[#f5ebe0]/5 backdrop-blur-sm rounded-2xl p-6 md:p-10 border border-amber-200/15">
            <p className="font-serif text-amber-200/70 leading-relaxed text-sm md:text-base mb-4">
              {content.intro}
            </p>

            <p className="font-serif text-amber-200/70 leading-relaxed text-sm md:text-base mb-8 italic">
              {content.disclaimer}
            </p>

            <div className="space-y-6 font-serif text-amber-200/70 leading-relaxed text-sm md:text-base">
              {content.sections.map((section, idx) => (
                <section key={idx}>
                  <h2 className="font-sans text-lg text-[#f5ebe0]/85 mb-3">{section.title}</h2>
                  {renderContent(section.content)}
                </section>
              ))}
            </div>

            {/* Support CTA — replaces the legacy email and routes users to the contact form */}
            <div className="mt-10 flex justify-center">
              <Link
                to={language === 'ru' ? '/ru/contact' : language === 'es' ? '/es/contact' : '/contact'}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#b8956b] hover:bg-[#a8855b] text-white font-sans transition-colors"
                data-testid="terms-support-button"
              >
                {content.supportButton}
              </Link>
            </div>
          </div>
        </div>
      </main>

      <footer className="relative z-10 py-8 px-6 border-t border-amber-200/10">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-amber-200/30 font-sans text-xs">© 2026 Nimny LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
